import { apiRequest, SUPABASE_URL, SUPABASE_ANON_KEY } from './apiUtils';
import { Order, OrderDocument, OrderPayment, OrderSearchParams, Property } from "@/models";

interface OrderResponse {
  id: number;
  association_id: number;
  property_id: number | null;
  association: {
    id: number;
    name: string;
  };
  customer_id: number;
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  status: string;
  total_amount: number;
  processing_type: string;
  created_at: string;
  updated_at: string;
  requested_documents: OrderDocument[];
  payments: OrderPayment[];
}

interface CustomerOrderResponse {
  success: boolean;
  order: Order;
}

interface PaymentRequest {
  payment_reference: string;
}

interface PaymentResponse {
  success: boolean;
  message: string;
  payment: OrderPayment;
}

interface RefundRequest {
  items: {
    item_id: number;
    quantity: number;
  }[];
}

interface RefundResponse {
  success: boolean;
  message: string;
  refund: {
    id: number;
    order_id: number;
    amount: number;
    status: string;
    created_at: string;
  };
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const orderApi = {
  /**
   * Get a list of orders with optional filtering
   */
  async list(params?: OrderSearchParams): Promise<PaginatedResponse<Order>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              // For status, we need to handle each value separately since it's an enum
              if (key === 'status') {
                value.forEach(status => queryParams.append('status', status));
              } else {
                value.forEach(v => queryParams.append(key, v));
              }
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
      }
      
      const url = `${SUPABASE_URL}/orders?${queryParams.toString()}`;
      console.log(`Fetching orders with URL: ${url}`);
      const response = await apiRequest<PaginatedResponse<Order>>(url);
      
      console.log("Orders API response:", response);
      
      if (!response) {
        return {
          data: [],
          total: 0,
          page: 1,
          pageSize: params?.limit || 10,
          totalPages: 0
        };
      }

      // Backend now returns the complete paginated structure, so we use it directly
      return {
        data: response.data,
        total: response.total,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages
      };
    } catch (error) {
      console.error('Error fetching orders:', error);
      throw error;
    }
  },

  /**
   * Get an order by ID
   */
  async getOrder(orderId: number): Promise<Order> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}`;
      return await apiRequest<Order>(url);
    } catch (error) {
      console.error(`Error fetching order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get a backoffice order by ID (with more details)
   */
  async getBackofficeOrder(orderId: number): Promise<Order> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}`;
      return await apiRequest<Order>(url);
    } catch (error) {
      console.error(`Error fetching backoffice order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get a customer order by ID
   */
  async getCustomerOrder(orderId: number): Promise<CustomerOrderResponse> {
    try {
      const url = `${SUPABASE_URL}/customer-orders/${orderId}`;
      return await apiRequest<CustomerOrderResponse>(url);
    } catch (error) {
      console.error(`Error fetching customer order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get order payments by order ID
   */
  async getOrderPayments(orderId: number): Promise<OrderPayment[]> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}/payments`;
      console.log(`Fetching payments from URL: ${url}`);
      return await apiRequest<OrderPayment[]>(url);
    } catch (error) {
      console.error(`Error fetching payments for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Process a payment for an order
   */
  async processPayment(orderId: number, paymentData: PaymentRequest): Promise<PaymentResponse> {
    try {
      const url = `${SUPABASE_URL}/customer-orders/${orderId}/pay`;
      return await apiRequest<PaymentResponse>(url, {
        method: 'POST',
        body: paymentData
      });
    } catch (error) {
      console.error(`Error processing payment for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Get properties associated with an association
   */
  async getAssociationProperties(associationId: number): Promise<Property[]> {
    try {
      const url = `${SUPABASE_URL}/properties?association_id=${associationId}`;
      return await apiRequest<Property[]>(url);
    } catch (error) {
      console.error(`Error fetching properties for association ${associationId}:`, error);
      return [];
    }
  },

  /**
   * Update an order's status
   */
  async updateStatus(orderId: number, status: string, note?: string): Promise<Order> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}`;
      const result = await apiRequest<Order>(url, {
        method: 'PATCH',
        body: { status }
      });
      
      if (note) {
        const notesUrl = `${SUPABASE_URL}/orders/${orderId}/notes`;
        await apiRequest<any>(notesUrl, {
          method: 'POST',
          body: { note }
        });
      }
      
      return result;
    } catch (error) {
      console.error(`Error updating order ${orderId} status:`, error);
      throw error;
    }
  },

  /**
   * Add a note to an order
   */
  async addOrderNote(orderId: number, note: string): Promise<any> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}/notes`;
      return await apiRequest<any>(url, {
        method: 'POST',
        body: { note }
      });
    } catch (error) {
      console.error(`Error adding note to order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Download a document from an order
   */
  async downloadDocument(orderId: number, documentId: number): Promise<Blob> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}/documents/${documentId}/download`;
      return await apiRequest<Blob>(url, { responseType: 'blob' });
    } catch (error) {
      console.error(`Error downloading document ${documentId} for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * List attachments for an order
   */
  async listOrderAttachments(orderId: number) {
    try {
      const res = await fetch(
        `/rest/v1/order_attachments?order_id=eq.${orderId}&order=created_at.desc`,
        {
          headers: {
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch order attachments");
      return await res.json();
    } catch (err) {
      console.error("Error listing order attachments:", err);
      return [];
    }
  },

  /**
   * Upload a new attachment to the order
   */
  async uploadOrderAttachment(orderId: number, file: File): Promise<any> {
    // Implemented via frontend w/ supabase storage; keeping stub for API completeness
    throw new Error("Please use Supabase SDK to upload order attachments.");
  },

  /**
   * Export orders to CSV
   * This is a client-side operation that formats order data for CSV export
   */
  async exportToCSV(orders: Order[], filename: string = 'orders_export.csv'): Promise<string> {
    try {
      // This method is implemented in the UI components
      // We keep it here for API completeness
      return '';
    } catch (error) {
      console.error('Error exporting orders to CSV:', error);
      throw error;
    }
  },

  /**
   * Process a refund for an order
   */
  async processRefund(orderId: number, refundData: RefundRequest): Promise<RefundResponse> {
    try {
      const url = `${SUPABASE_URL}/orders/${orderId}/refund`;
      return await apiRequest<RefundResponse>(url, {
        method: 'POST',
        body: refundData
      });
    } catch (error) {
      console.error(`Error processing refund for order ${orderId}:`, error);
      throw error;
    }
  },

  /**
   * Create an update order based on an existing order
   */
  async createUpdateOrder(orderId: number, requestedDocumentId: number): Promise<any> {
    try {
      const url = `${SUPABASE_URL}/create-update-order`;
      return await apiRequest<any>(url, {
        method: 'POST',
        body: {
          order_id: orderId,
          requested_document_id: requestedDocumentId
        }
      });
    } catch (error) {
      console.error(`Error creating update order for order ${orderId}:`, error);
      throw error;
    }
  }
};
